"use client";
import PureButton from "@/components/Popup_Assistant/Button/PureButton";
import { useSubscription } from "@/helper/useSubscription";
import {
  optionRoleSubscription,
  OPTIONS_SUBSCRIPTION,
} from "@/models/subscription";
import { User } from "@/models/user";
import IconAI from "@components/icons/IconAI";
import { useCoAgent, useCopilotAction } from "@copilotkit/react-core";
import { CopilotSidebar } from "@copilotkit/react-ui";
import "@copilotkit/react-ui/styles.css";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SendIcon from "@mui/icons-material/Send";
import { AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { memo } from "react";
import Header from "./Header";
import Input from "./Input";
import "./styles.css";
import Window from "./Window";
interface PopupAssistantProps {
  user?: User | null;
  locale: string;
}

const PopupAssistant = ({ user, locale }: PopupAssistantProps) => {
  const t = useTranslations("messages");
  const { hasPermission } = useSubscription();
  const hostName = (process.env.NEXT_PUBLIC_BASE_URL || "")
    .split("//")[1]
    .split(".")[0];
  const tenant_id = hostName || null;
  useCopilotAction({
    name: "search_relevant_documents",
    available: "disabled", // Don't allow the agent or UI to call this tool as its only for rendering
    render: ({ status, args }) => {
      return (
        <p className="text-gray-500 mt-2">
          {status !== "complete" && "🔎 Searching data..."}
          {status === "complete" && `🔎 Searching done`}
        </p>
      );
    },
  });
  // useCopilotChatSuggestions({
  //   instructions:
  //     "Suggest quick-reply suggestions for what the user might ask or do next based on the answer. Skip if the answer is not relevant to a research study, data, report. The title of suggestion should be short and focused on the main information, not the general one.",
  //   minSuggestions: 0,
  //   maxSuggestions: 2,
  // });

  const getInitialMessage = () => {
    if (!user) {
      const loginUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/login`;
      const registerUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/register`;
      // Hardcode HTML trong function thay vì translation
      return `<strong>${t("copilotkit_welcome")}</strong><br/>
${t("copilotkit_description")}<br/><br/>
${t("copilotkit_please")}<a href="${registerUrl}" style="color: #1976d2; text-decoration: underline;">${t("copilotkit_register_text")}</a>${t("copilotkit_login_instruction")} <a href="${loginUrl}" style="color: #1976d2; text-decoration: underline;">${t("copilotkit_login_text")}</a>${t("copilotkit_use_text")}`;
    }

    return `${t("copilotkit_assistant_initial")} ${!hasPermission(OPTIONS_SUBSCRIPTION.AI) ? t("copilotkit_free_version") : ""}`;
  };

  const { running } = useCoAgent({
    name: "data_search_chatbot",
    config: {
      configurable: {
        user_info: {
          id: user?.id || "",
          email: user?.email || "",
          fullname: user?.fullName
            ? `${user?.fullName || ""}`
            : `${user?.firstName || ""} ${user?.lastName || ""}`,
          language: user?.language || locale,
          license: user?.license
            ? {
                name: user?.license.subscription?.name || "",
                validUntil: user?.license?.expiredDate || "",
                trial: user?.license.isTrial || false,
                isOrganizationLicense: !!user?.license?.organizationId || false,
                options: optionRoleSubscription
                  .filter((option) =>
                    user?.license?.subscription?.options
                      ?.map((item) => item)
                      .includes(option?.id)
                  )
                  .map((item) => item?.name),
              }
            : null,
          organization: user?.organization
            ? {
                id: user?.organization?.organization?.id || 0,
                name: user?.organization?.organization?.name || "",
              }
            : null,
          tenant_id: tenant_id,
        },
      },
    },
  });

  return (
    <AnimatePresence>
      <CopilotSidebar
        Header={Header}
        Window={Window}
        Button={PureButton}
        Input={Input}
        icons={{
          openIcon: (
            <IconAI
              height={32}
              width={35}
              viewBox="0 0 35 32"
              sx={{
                paddingLeft: "8px",
              }}
            />
          ),
          closeIcon: (
            <IconAI
              height={32}
              width={35}
              viewBox="0 0 35 32"
              sx={{
                paddingLeft: "8px",
              }}
            />
          ),
          headerCloseIcon: <KeyboardArrowDownIcon />,
          sendIcon: <SendIcon />,
        }}
        defaultOpen={false}
        instructions={`${t("copilotkit_assistant_instructions")}`}
        labels={{
          title: t("copilotkit_assistant_title"),
          initial: getInitialMessage(),
        }}
      />
    </AnimatePresence>
  );
};

export default memo(PopupAssistant);
